// Running loan (spec §12–§14): figures, the EMI schedule and every action —
// pay now, part-prepay, foreclose, top-up, EMI day and repayment method.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import {
  changeEmiDay,
  changeRepaymentMethod,
  exitDuringCoolingOff,
  forecloseLoan,
  getLoan,
  getSchedule,
  partPrepay,
  payEmiNow,
  topUpLoan,
} from '@/api/loans';
import { ApiError } from '@/api/client';
import type { LoanRecord, RepaymentMethod, ScheduleEntry } from '@/types/loan.types';
import { productById } from '@/mock/loanData';
import { formatLong } from '@/mock/clock';
import { isRevolving, nextUnpaid, paidEmis } from '@/utils/loans';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import { ErrorLine, LoanStatusChip, inr } from './loanShared';
import RevolvingPanel from './RevolvingPanel';

const EMI_DAYS = [1, 5, 10];

const LoanDetails = () => {
  const { loanId = '' } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState<LoanRecord | null>(null);
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  // Inline action forms
  const [prepayAmount, setPrepayAmount] = useState('');
  const [prepayMode, setPrepayMode] = useState<'REDUCE_EMI' | 'REDUCE_TENURE'>('REDUCE_EMI');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [openAction, setOpenAction] = useState<'prepay' | 'topup' | 'emiday' | 'method' | null>(null);

  const load = useCallback(() => {
    getLoan(loanId)
      .then(async (l) => {
        setLoan(l);
        if (l.schedule.length > 0) setSchedule(await getSchedule(l.id).catch(() => []));
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the loan.'));
  }, [loanId]);

  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice(ok);
      setOpenAction(null);
      setPrepayAmount('');
      setTopUpAmount('');
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The action failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!loan) {
    return (
      <div className="nova-loans-page">
        {error ? <ErrorLine message={error} /> : <Loader rows={4} />}
      </div>
    );
  }

  const product = productById(loan.productId);
  const term = !isRevolving(loan);
  const running = ['active', 'overdue', 'moratorium', 'unutilised'].includes(loan.state);
  const cooling = loan.state === 'coolingOff';
  const unpaid = nextUnpaid(loan);
  const closed = ['closed', 'foreclosed'].includes(loan.state);

  return (
    <div className="nova-loans-page">
      <div className="nova-loans-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/borrow')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{product.name}</h2>
      </div>

      <div className="nova-loan-cardhead">
        <span>
          <strong>{inr(loan.amountPaise)}</strong>
          <span className="nova-loan-row-sub"> {loan.ratePaPct.toFixed(2)}% p.a.</span>
        </span>
        <LoanStatusChip state={loan.state} />
      </div>

      {cooling && loan.coolingOffUntil && (
        <div className="nova-loan-panel is-info">
          <p>
            <FontAwesomeIcon icon={faTriangleExclamation} /> Cooling-off until {formatLong(loan.coolingOffUntil)}.
            Return the principal plus interest for the days held and the loan closes; the fee is not refunded.
          </p>
          <Button
            variant="outline"
            isLoading={busy}
            onClick={() => run(() => exitDuringCoolingOff(loan.id), 'Loan closed during the cooling-off period.')}
          >
            Exit this loan
          </Button>
        </div>
      )}

      {loan.marginCall && (
        <div className="nova-loan-panel is-bad">
          <p>
            <FontAwesomeIcon icon={faTriangleExclamation} /> Your collateral value has fallen. Repay{' '}
            {inr(loan.marginCall.shortfallPaise)} or pledge more securities by {formatLong(loan.marginCall.dueAt)}.
          </p>
        </div>
      )}

      {loan.state === 'overdue' && (
        <div className="nova-loan-panel is-bad">
          <p>
            <FontAwesomeIcon icon={faTriangleExclamation} /> An EMI is overdue. Pay it now to avoid more charges.
          </p>
        </div>
      )}

      {/* Key figures */}
      <div className="nova-loan-facts">
        {term ? (
          <div className="nova-loan-fact">
            <span>Outstanding</span>
            <strong>{inr(loan.outstandingPaise)}</strong>
          </div>
        ) : (
          <>
            <div className="nova-loan-fact">
              <span>Limit</span>
              <strong>{inr(loan.amountPaise)}</strong>
            </div>
            <div className="nova-loan-fact">
              <span>Used</span>
              <strong>{inr(loan.usedPaise ?? 0)}</strong>
            </div>
            <div className="nova-loan-fact">
              <span>Available</span>
              <strong>{inr(loan.amountPaise - (loan.usedPaise ?? 0))}</strong>
            </div>
          </>
        )}
        {loan.emiPaise !== undefined && (
          <div className="nova-loan-fact">
            <span>EMI</span>
            <strong>{inr(loan.emiPaise)}</strong>
          </div>
        )}
        {loan.nextEmiDate && running && (
          <div className="nova-loan-fact">
            <span>Next EMI</span>
            <strong>{formatLong(loan.nextEmiDate)}</strong>
          </div>
        )}
        {loan.tenureMonths && (
          <div className="nova-loan-fact">
            <span>EMIs paid</span>
            <strong>{paidEmis(loan)} of {loan.schedule.length || loan.tenureMonths}</strong>
          </div>
        )}
        {loan.lateChargesPaise > 0 && (
          <div className="nova-loan-fact">
            <span>Late charges</span>
            <strong>{inr(loan.lateChargesPaise)}</strong>
          </div>
        )}
        {loan.bulletDuePaise !== undefined && loan.bulletDueDate && (
          <div className="nova-loan-fact">
            <span>Bullet payment due</span>
            <strong>{inr(loan.bulletDuePaise)} · {formatLong(loan.bulletDueDate)}</strong>
          </div>
        )}
        {loan.moratoriumUntil && (
          <div className="nova-loan-fact">
            <span>Moratorium until</span>
            <strong>{formatLong(loan.moratoriumUntil)}</strong>
          </div>
        )}
        {loan.stagesPaid !== undefined && (
          <div className="nova-loan-fact">
            <span>Stages released</span>
            <strong>{loan.stagesPaid} of 4</strong>
          </div>
        )}
        {loan.goldValuePaise !== undefined && (
          <div className="nova-loan-fact">
            <span>Gold value</span>
            <strong>{inr(loan.goldValuePaise)}</strong>
          </div>
        )}
        {loan.goldRelease && (
          <div className="nova-loan-fact">
            <span>Gold</span>
            <strong>{loan.goldRelease.state === 'PLEDGED' ? 'Pledged with the bank' : loan.goldRelease.state}</strong>
          </div>
        )}
      </div>

      {/* Revolving actions */}
      {!term && running && <RevolvingPanel loan={loan} onChanged={load} />}

      {/* Term actions */}
      {term && running && (
        <>
          {unpaid && loan.emiPaise !== undefined && (
            <Button
              onClick={() =>
                run(
                  () => payEmiNow(loan.id),
                  `EMI ${inr(loan.emiPaise! + (unpaid.failed ? loan.lateChargesPaise : 0))} paid.`
                )
              }
              isLoading={busy}
            >
              Pay EMI now — {inr(loan.emiPaise + (unpaid.failed ? loan.lateChargesPaise : 0))}
              {unpaid.failed ? ' (incl. late charge)' : ''}
            </Button>
          )}

          <div className="nova-loan-actions">
            {loan.state === 'active' && (
              <>
                <button type="button" className="nova-loan-link" onClick={() => setOpenAction(openAction === 'prepay' ? null : 'prepay')}>
                  Part-prepay
                </button>
                {['PERSONAL', 'VEHICLE', 'HOME', 'LAP', 'BUSINESS'].includes(loan.productId) && (
                  <button type="button" className="nova-loan-link" onClick={() => setOpenAction(openAction === 'topup' ? null : 'topup')}>
                    Top up
                  </button>
                )}
                <button
                  type="button"
                  className="nova-loan-link is-danger"
                  onClick={() =>
                    run(
                      () => forecloseLoan(loan.id),
                      'Loan foreclosed. The closure letter will reach your email.'
                    )
                  }
                >
                  Foreclose
                </button>
              </>
            )}
            {loan.emiDay !== undefined && (
              <button type="button" className="nova-loan-link" onClick={() => setOpenAction(openAction === 'emiday' ? null : 'emiday')}>
                Change EMI day
              </button>
            )}
            <button type="button" className="nova-loan-link" onClick={() => setOpenAction(openAction === 'method' ? null : 'method')}>
              Repayment method
            </button>
          </div>

          {openAction === 'prepay' && (
            <div className="nova-loan-panel">
              <h3 className="nova-loan-section">Part-prepay</h3>
              <label className="nova-loan-field">
                <span>Amount (₹)</span>
                <input
                  className="form-control"
                  inputMode="numeric"
                  value={prepayAmount}
                  onChange={(e) => setPrepayAmount(e.target.value.replace(/[^\d]/g, ''))}
                />
              </label>
              <div className="nova-loan-chips">
                {(['REDUCE_EMI', 'REDUCE_TENURE'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`nova-loan-chipbtn ${prepayMode === m ? 'is-active' : ''}`}
                    onClick={() => setPrepayMode(m)}
                  >
                    {m === 'REDUCE_EMI' ? 'Smaller EMIs' : 'Shorter tenure'}
                  </button>
                ))}
              </div>
              <Button
                isLoading={busy}
                disabled={!prepayAmount}
                onClick={() => run(() => partPrepay(loan.id, Number(prepayAmount), prepayMode), 'Part-prepayment done.')}
              >
                Prepay
              </Button>
            </div>
          )}

          {openAction === 'topup' && (
            <div className="nova-loan-panel">
              <h3 className="nova-loan-section">Top up</h3>
              <label className="nova-loan-field">
                <span>Extra amount (₹)</span>
                <input
                  className="form-control"
                  inputMode="numeric"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value.replace(/[^\d]/g, ''))}
                />
              </label>
              <Button
                isLoading={busy}
                disabled={!topUpAmount}
                onClick={() => run(() => topUpLoan(loan.id, Number(topUpAmount)), 'Top-up disbursed to your account.')}
              >
                Top up
              </Button>
            </div>
          )}

          {openAction === 'emiday' && (
            <div className="nova-loan-panel">
              <h3 className="nova-loan-section">EMI day of the month</h3>
              <div className="nova-loan-chips">
                {EMI_DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`nova-loan-chipbtn ${loan.emiDay === day ? 'is-active' : ''}`}
                    disabled={busy}
                    onClick={() => run(() => changeEmiDay(loan.id, day), `EMI day changed to the ${day}th.`)}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}

          {openAction === 'method' && (
            <div className="nova-loan-panel">
              <h3 className="nova-loan-section">Repayment method</h3>
              {(['AUTO_DEBIT', 'ENACH', 'UPI_AUTOPAY'] as RepaymentMethod[]).map((m) => (
                <label key={m} className="nova-loan-holding">
                  <input
                    type="radio"
                    name="change-method"
                    checked={loan.repaymentMethod === m}
                    disabled={busy}
                    onChange={() => run(() => changeRepaymentMethod(loan.id, m), 'Repayment method updated.')}
                  />
                  <span className="nova-loan-holding-text">
                    <strong>
                      {m === 'AUTO_DEBIT' ? 'Auto-debit from NovaBank' : m === 'ENACH' ? 'eNACH' : 'UPI Autopay'}
                    </strong>
                  </span>
                </label>
              ))}
            </div>
          )}
        </>
      )}

      {closed && (
        <div className="nova-loan-panel">
          <p>
            This loan is {loan.state}. The closure letter and, where applicable, the release of your collateral
            are on their way.
          </p>
        </div>
      )}

      {/* Schedule */}
      {schedule.length > 0 && (
        <>
          <h3 className="nova-loan-section">EMI schedule</h3>
          <div className="nova-loan-tablewrap">
            <table className="nova-loan-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Due</th>
                  <th>EMI</th>
                  <th>Balance after</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((s) => (
                  <tr key={s.n} className={s.failed ? 'is-failed' : ''}>
                    <td>{s.n}</td>
                    <td>{formatLong(s.dueDate)}</td>
                    <td>{inr(s.emiPaise)}</td>
                    <td>{inr(s.balanceAfterPaise)}</td>
                    <td>
                      {s.paidAt
                        ? `Paid ${formatLong(s.paidAt)}`
                        : s.failed
                          ? 'Failed — pay now'
                          : 'Upcoming'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {notice && <p className="nova-loan-ok">{notice}</p>}
      <ErrorLine message={error} />
    </div>
  );
};

export default LoanDetails;
